import React, { useEffect, useState } from "react";
import axios from "axios";
import { MutatingDots } from "react-loader-spinner";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import Package from "./component/Package";
import Cart from "./component/Cart";
import getLogoUrl from "./utils/get-image-url";
import { formattedTranslationValue } from "./utils/formatted-translation-value";
import { getFormattedDateTime } from "./utils/getFormattedDate";
import PackageHorizontal from "./component/PackageHorizontal";
import { calculateIsDarkColor } from "./utils/color";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import HomeIcon from "@mui/icons-material/Home";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { FormattedMessage, useIntl } from "react-intl";
import TagManager from "react-gtm-module";
import { isInteger, sum } from "lodash-es";
import FloatingCart from "./component/FloatingCart";
import PoweredBy from "./component/PoweredBy";
import CollapsibleDescription from "./component/CollapsibleDescription";
import { cleanEmptyHTMLTags } from "./hooks/cleanEmptyHTMLTags";

const RegisterWidget = ({
	language,
	eventID,
	organiserID,
	color,
	displayTitle,
	formID,
	grid,
	view,
	headerImage,
	footerImage,
}) => {
	const intl = useIntl();
	const eventButtonColor = calculateIsDarkColor(color) ? "#fff" : "#000";

	const REACT_APP_API_URL = process.env.REACT_APP_API_URL;
	const REACT_APP_ENV = process.env.REACT_APP_ENV;
	const REACT_APP_API_URL_TEST = process.env.REACT_APP_API_URL_TEST;
	const REACT_APP_WEB_URL = process.env.REACT_APP_WEB_URL;

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [form, setForm] = useState([]);
	const [pkgs, setPkgs] = useState([]);
	const [cart, setCart] = useState([]);
	const [cartOpen, setCartOpen] = useState(false);

	const totalQuantity = sum(
		Object.values(cart ?? []).map((item) => item.quantity)
	);
	const minQuantity = form?.minCartItemCount ?? 0;
	const maxQuantity = form?.maxCartItemCount ?? Infinity;
	const notWithinRange = Boolean(
		isInteger(totalQuantity) &&
			(totalQuantity < minQuantity || totalQuantity > maxQuantity)
	);

	useEffect(() => {
		if (form?.event)
			TagManager.dataLayer({
				dataLayer: {
					event: "kapital_events_view_content_form",
					kapital_events_event_name: form?.event?.name,
				},
			});
	}, [form]);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const url =
					REACT_APP_ENV === "prod" ? REACT_APP_API_URL : REACT_APP_API_URL_TEST;
				const res = await axios({
					method: "get",
					url: `${url}/api/register-form/public/${formID}?basic=false`,
					headers: {
						"Content-Type": "application/json",
						eventID: eventID,
						organiserID: organiserID,
					},
					data: {},
				});
				if (res.status === 200) {
					setForm(res.data || []);
					setPkgs(res.data.packages || []);
				} else {
					setError(true);
					setLoading(true);
				}
			} catch (error) {
				setError(true);
			} finally {
				setError(false);
				setLoading(false);
			}
		};
		fetchData();
	}, [
		formID,
		organiserID,
		eventID,
		REACT_APP_ENV,
		REACT_APP_API_URL_TEST,
		REACT_APP_API_URL,
	]);

	const tickets = pkgs;

	const description = formattedTranslationValue(
		form?.translations,
		"description",
		language
	);
	// Açıklamada hiç içerik yoksa (sadece boş etiketler) "Etkinlik Hakkında" gösterilmez.
	// İçerik değiştirilmez, sadece görünürlük kontrol edilir.
	const hasDescription = Boolean(cleanEmptyHTMLTags(description));

	const pushDataLayerAddToCart = (cart, item) => {
		try {
			if (form?.event)
				TagManager.dataLayer({
					dataLayer: {
						event: "kapital_events_add_to_cart",
						kapital_events_event_name: form?.event?.name,
						kapital_events_cart_items: cart.map((pp) => ({
							content_id: pp.package.id,
							content_name: formattedTranslationValue(
								pp.package.translations,
								"name",
								language
							),
							num_items: pp.quantity,
							content_price: pp.price,
						})),
						kapital_events_cart_count: sum(cart.map((pp) => pp.quantity)),
						kapital_events_cart_total: sum(
							cart.map((pp) => parseFloat(`${pp.price}`) * pp.quantity)
						),
						kapital_events_cart_currency: item.currencyCode,
					},
				});
		} catch (error) {
			console.log(error);
		}
	};

	const handleCartUpdate = (updatedItem) => {
		setCart((prevCart) => {
			const existingItemIndex = prevCart.findIndex(
				(item) => item.id === updatedItem.id
			);

			if (existingItemIndex !== -1) {
				const updatedCart = [...prevCart];
				updatedCart[existingItemIndex] = {
					...updatedCart[existingItemIndex],
					quantity: updatedCart[existingItemIndex].quantity + 1,
				};
				pushDataLayerAddToCart(updatedCart, updatedItem);
				return updatedCart;
			} else {
				const newCart = [...prevCart, { ...updatedItem, quantity: 1 }];
				pushDataLayerAddToCart(newCart, updatedItem);
				return newCart;
			}
		});
	};

	const removeFromCart = (id) => {
		setCart((prevCart) => prevCart.filter((item) => item.id !== id));
	};

	if (loading || error || form.length === 0) {
		return (
			<Box className="widget-spinner">
				<MutatingDots
					visible={true}
					height="100"
					width="100"
					color={color ?? "#4fa94d"}
					secondaryColor={color ?? "#4fa94d"}
					radius="12.5"
					ariaLabel="mutating-dots-loading"
					wrapperStyle={{}}
					wrapperClass=""
				/>
			</Box>
		);
	}

	return (
		<main
			className="widget-block"
			style={{
				"--color": color,
			}}
		>
			<FloatingCart
				formID={formID}
				cartItems={cart}
				minCartItemCount={form?.minCartItemCount}
				maxCartItemCount={form?.maxCartItemCount}
			/>
			<Box className="widget-block--container">
				<Box className="register-toolbar">
					<button
						type="button"
						className="register-toolbar__btn register-toolbar__tickets no-toggle"
						onClick={() => {
							window.open(`${REACT_APP_WEB_URL}/participant/login`, "_blank");
						}}
					>
						<ConfirmationNumberIcon className="register-toolbar__icon" />
						<span className="register-toolbar__label">
							<FormattedMessage id="my-ticket" />
						</span>
					</button>

					<Box className="register-toolbar__cart-wrap">
						<Cart
							cartItems={cart}
							setCart={setCart}
							removeFromCart={removeFromCart}
							currency={form?.currency}
							formID={formID}
							cartOpen={cartOpen}
							setCartOpen={setCartOpen}
							language={language}
							color={color}
							free={form?.participationType === "free"}
							notWithinRange={notWithinRange}
							tooltip={
								notWithinRange
									? intl.formatMessage(
											{
												id:
													maxQuantity > 0
														? "cart_items_count_not_within_limit_with_max"
														: "cart_items_count_not_within_limit",
											},
											{
												min: minQuantity,
												max: maxQuantity,
											}
									  )
									: null
							}
						/>
					</Box>
				</Box>
				<Box className="widget-block--form">
					{/*
						Boarding-pass layout ported from the app.kapital.com.tr
						registration page redesign: a photo panel and an info panel
						split by a dashed "tear line", so the widget reads as the top
						of the same ticket. With no photo the info panel just takes
						the full width instead of a decorative placeholder.
					*/}
					{displayTitle && (
						<Box
							className={`event-banner${
								!(form?.headerPhoto && headerImage)
									? " event-banner--no-photo"
									: ""
							}`}
						>
							{form?.headerPhoto && headerImage && (
								<>
									<Box
										className="event-banner__photo"
										sx={{
											backgroundImage: `url(${getLogoUrl(form.headerPhoto)})`,
										}}
									/>
									<Box className="event-banner__divider" aria-hidden="true" />
								</>
							)}

							<Box className="event-banner__info">
								<Typography
									variant="span"
									component="h1"
									className="event-banner__title"
								>
									{formattedTranslationValue(form?.translations, "name", language)}
								</Typography>

								<Box className="event-banner__meta">
									{form?.event?.venue && (
										<Box className="event-banner__meta-row">
											<Box
												className="event-banner__meta-icon"
												sx={{ backgroundColor: color, color: eventButtonColor }}
											>
												<HomeIcon sx={{ fontSize: 16 }} />
											</Box>
											<Typography variant="span" className="event-banner__meta-text">
												{form.event.venue}
											</Typography>
										</Box>
									)}
									{(form?.event?.address || form?.event?.country) && (
										<Box className="event-banner__meta-row">
											<Box
												className="event-banner__meta-icon"
												sx={{ backgroundColor: color, color: eventButtonColor }}
											>
												<PlaceIcon sx={{ fontSize: 16 }} />
											</Box>
											<Typography variant="span" className="event-banner__meta-text">
												{form.event.address
													? `${form.event.address}, ${form.event.country}`
													: form.event.country}
											</Typography>
										</Box>
									)}
									{form?.event?.startDate && form?.event?.endDate && (
										<Box className="event-banner__meta-row">
											<Box
												className="event-banner__meta-icon"
												sx={{ backgroundColor: color, color: eventButtonColor }}
											>
												<CalendarMonthIcon sx={{ fontSize: 16 }} />
											</Box>
											<Typography variant="span" className="event-banner__meta-text">
												{getFormattedDateTime(
													form.event.startDate,
													form.event.dateFormat,
													form.event.timeFormat,
													form.event.timeZone
												)}
												{" → "}
												{getFormattedDateTime(
													form.event.endDate,
													form.event.dateFormat,
													form.event.timeFormat,
													form.event.timeZone
												)}
												{form.event.timeZone ? ` (${form.event.timeZone})` : ""}
											</Typography>
										</Box>
									)}
								</Box>
							</Box>
						</Box>
					)}
					{hasDescription && (
						<CollapsibleDescription
							html={description}
							iconColor={eventButtonColor}
						/>
					)}
					{view === "horizontal" ? (
						<Box
							className="widget-block--form--cart--container--horizontal"
							sx={{
								display: "grid",
							}}
						>
							{tickets &&
								tickets.map((pkg, key) => (
									<PackageHorizontal
										pkg={pkg}
										currency={form?.currency}
										event={form}
										key={key}
										color={color}
										onCartUpdate={handleCartUpdate}
										cart={cart}
										setCartOpen={setCartOpen}
										free={form?.participationType === "free"}
										language={language}
									/>
								))}
						</Box>
					) : (
						<Box
							className="widget-block--form--cart--container"
							sx={{
								display: "grid",
								gap: "var(--section-gap, 24px)",
								gridTemplateColumns: {
									xs: "repeat(1, minmax(0, 1fr))",
									md: `repeat(${grid}, minmax(0, 1fr))`,
									lg: `repeat(${grid}, minmax(0, 1fr))`,
								},
							}}
						>
							{tickets &&
								tickets.map((pkg, key) => (
									<Package
										pkg={pkg}
										currency={form?.currency}
										form={form}
										key={key}
										color={color}
										onCartUpdate={handleCartUpdate}
										cart={cart}
										setCartOpen={setCartOpen}
										free={form?.participationType === "free"}
										language={language}
									/>
								))}
						</Box>
					)}

					{form?.footerPhoto && footerImage && (
						<Box className="widget-block--form--footerImgaeContainer">
							<img
								className="widget-block--form--headerImgaeContainer--image"
								src={getLogoUrl(form?.footerPhoto)}
								alt={form?.name}
							/>
						</Box>
					)}
				</Box>
			</Box>
			<PoweredBy />
		</main>
	);
};

export default RegisterWidget;
